import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import { AppRoutes } from "../../../routes/AppRoutes";
import { useEditSubscriptionMutation, useEditOptionMutation, useLazyGetRetentionOffersQuery } from "../../../rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";

import { useLocation } from "react-router-dom";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";

import ReviewCardList, { ReviewCardListHandle } from "../../organisms/ReviewCardList/ReviewCardList";
import Footer from "../../templates/Footer/Footer";
import Header from "../../templates/Header/Header";
import StyledButton from "../../atoms/StyledButton/StyledButton";

const ManageSubscriptionsCancelReason = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState<string>("");
  const [manageOptionReason, { isLoading: manageLoadingEditOption }] = useEditOptionMutation();
  const [manageSubscriptionReason, { isLoading: manageLoadingEditSubscription }] = useEditSubscriptionMutation();
  const [fetchRetentionOffers, { isLoading: retentionOffersLoading }] = useLazyGetRetentionOffersQuery();
  const [isInSubLevel, setIsInSubLevel] = useState(false);
  const reviewCardListRef = useRef<ReviewCardListHandle>(null);

  const handleGoBack = () => {
    if (isInSubLevel) {
      reviewCardListRef.current?.exitSubLevel();
      setSelectedReason(null);
      setReasonText("");
    } else {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_CANCEL_CONFIRM, { state: location.state });
    }
  };

  const handleReviewSelection = (reasonKey: string, textValue?: string) => {
    console.log("Selected review reason:", reasonKey, "Text:", textValue);
    setSelectedReason(reasonKey);
    setReasonText(textValue || "");
  };

  const handleContinue = async () => {
    const drilled = reviewCardListRef.current?.drillSelectedReason();
    if (drilled) {
      setSelectedReason(null);
      setReasonText("");
      return;
    }
    await handleDeactivate();
  };

  const handleDeactivate = async () => {
    if (location.state?.type === 'subscription') {
      const { data: retentionOffers } = await fetchRetentionOffers({ cancel_reason: selectedReason || "manually" });
      if (retentionOffers?.offers?.length) {
        navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_RETENTION_OFFERS, {
          state: {
            ...location.state,
            offers: retentionOffers.offers,
            cancelData: {
              cancel_reason: selectedReason || "manually",
              ...(reasonText && { cancel_explanation: reasonText })
            }
          }
        });
        return;
      }
      try {
        await manageSubscriptionReason({
          renewal_active: false,
          cancel_reason: selectedReason || "manually",
          ...(reasonText && { cancel_explanation: reasonText })
        }).unwrap();
        navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'cancellation_success', type: 'subscription' } });
      } catch (error) {
        navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
      }
    } else if (location.state?.type === 'option') {
      try {
        await manageOptionReason({
          option_code: location.state?.code,
          renewal_active: false,
          cancel_reason: selectedReason || "manually",
          ...(reasonText && { cancel_explanation: reasonText })
        }).unwrap();
        navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'cancellation_success', type: 'option', code: location.state?.code } });
      } catch (error) {
        navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
      }
    }
  };

return (
  <>
    <Header
      headerStep={{ context: "manage" }}
      title={
        location.state?.type !== 'subscription'
          ? t("subscription:MANAGE_PAGE.EDIT_OPTION.HEADER")
          : t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.HEADER")
      }
      goBack={handleGoBack}
    />
    <Box className="ManageSubscriptionsCancelReason">
      <ReviewCardList
        ref={reviewCardListRef}
        item={location.state}
        onSelectionChange={handleReviewSelection}
        onDrillStateChange={setIsInSubLevel}
      />
    </Box>
    <Footer headerStep={{ context: "manage" }}>
      <StyledButton
        children={t("subscription:FIRST_PAGE.BUTTON")}
        onClick={handleContinue}
        disabled={manageLoadingEditOption || manageLoadingEditSubscription || retentionOffersLoading || selectedReason === null}
      />
    </Footer>
  </>
);
};

export default ManageSubscriptionsCancelReason;